import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { labelForStat } from "@/utils/stats";
import { PrimaryButton } from "@/components/Button/PrimaryButton";
import { resolveItemArt } from "@/content/itemArt";
import type { ItemDefinition, InventoryItemView, ItemCategory } from "@/api/types";
import { ENGRAVE_META } from "@/utils/skillLabels";
import { useV2Content, findEngraveMaterials } from "@/content/combatV2Content";
import { RefineModal } from "@/components/RefineModal";
import { EngraveCeremonyModal } from "@/components/EngraveCeremonyModal";
import { repairCrack } from "@/api/endpoints/forge";
import { describeApiError } from "@/utils/errorMessages";
import { EquipmentAura, resolveEquipmentAuraColor } from "@/components/forge/EquipmentAura";
import { useForgeShimmer } from "@/components/forge/useForgeShimmer";
import { SalvageConfirmModal } from "@/components/forge/SalvageConfirmModal";

/**
 * 아이템 상세 카드. Shop / CharacterSheet / Event 에서 공용.
 *
 * 설계: [docs/art/item-detail-card.md](../../../docs/art/item-detail-card.md)
 * - 양피지·잉크 톤 유지
 * - 보는 재미: 아트 슬롯 + 희귀도별 시각 차등
 * - 읽는 재미: pitch(1줄) + lore(2-4문단) + 효과 + 요구사항
 *
 * 아이템 이미지는 `/art/items/{item.id}.png` 경로에서 로드. 없으면 카테고리 폴백 →
 * 최종적으로 희귀도 색상 그라디언트 + 카테고리 이모지로 대체.
 */

export type ItemContext =
    | "shop-buy"
    | "shop-sell"
    | "inventory-equip"
    | "inventory-consume"
    | "event-reward"
    | "readonly";

interface ItemDetailCardProps {
    item: ItemDefinition;
    /** inventory 컨텍스트에서 강화 수치·인스턴스 정보. 없으면 원본 definition 기준. */
    instance?: InventoryItemView | null;
    variant?: "full" | "compact";
    context?: ItemContext;
    /** shop 컨텍스트의 판매가/구매가. */
    price?: number;
    /** 구매 불가 (골드 부족 등). */
    canAfford?: boolean;
    /** 요구사항 체크용 — 현재 캐릭터 스탯. */
    currentStats?: Record<string, number>;
    /** 주요 액션 (구매/장착/사용/받기/판매). */
    onAction?: () => void;
    /** 액션 비활성. */
    actionDisabled?: boolean;
    /** 닫기 (모달 컨텍스트). */
    onClose?: () => void;
    /** 2026-04-24: 각인 변경 콜백 — 제공되면 EngraveBlock 에 "각인 변경" 버튼 노출.
     *  (instanceId + 새 engraveId + 선택적 materialInstanceId). 상위에서 API 호출. */
    onEngraveChange?: (nextEngraveId: string | null, materialInstanceId?: string | null) => void;
    /** 2026-04-24: 현재 캐릭터 보유 gold — 각인 비용 가능 여부 판정용. */
    currentGold?: number;
    /** 2026-04-24: 현재 인벤토리 — 각인 재료 소지 여부 판정용. undefined 면 재료 소모 비활성. */
    inventoryItems?: InventoryItemView[];
    /** 2026-04-25 Phase 32-B: 런 id — Phase 32 제련·각인 의식 모달 실행용. undefined 면 버튼 숨김. */
    runId?: string;
    /** 2026-04-25 Phase 32-B: 제련/각인 의식 완료 시 상위 화면 reload 콜백. */
    onForgeComplete?: () => void;
}

/**
 * 각인 부여·교체 비용. 백엔드 InventoryService.engraveForgeCost 와 동기화.
 *   always (상시) 200 · conditional (조건부) 140 · probabilistic (확률) 100.
 */
const ENGRAVE_FORGE_COST_DEFAULT = 120;

function engraveForgeCost(engraveId: string): number {
    switch (engraveId) {
        case "engrave.yunseul":
        case "engrave.byeolbit":
        case "engrave.haneul": return 200;
        case "engrave.miri-nae":
        case "engrave.dalbit":
        case "engrave.saebyeok": return 140;
        case "engrave.noeul":
        case "engrave.narae":
        case "engrave.yeoul":
        case "engrave.eoseureum":
        case "engrave.meari": return 100;
        default: return ENGRAVE_FORGE_COST_DEFAULT;
    }
}

export function ItemDetailCard({
    item,
    instance,
    variant = "full",
    context = "readonly",
    price,
    canAfford = true,
    currentStats,
    onAction,
    actionDisabled,
    onClose,
    onEngraveChange,
    currentGold,
    inventoryItems,
    runId,
    onForgeComplete,
}: ItemDetailCardProps) {
    const [engravePickerOpen, setEngravePickerOpen] = useState(false);
    const [refineModalOpen, setRefineModalOpen] = useState(false);
    const [ceremonyModalOpen, setCeremonyModalOpen] = useState(false);
    const [salvageModalOpen, setSalvageModalOpen] = useState(false);
    const rarityColor = paletteColor(item.rarityDisplay?.paletteToken);
    const rarityLabel = item.rarityDisplay?.labelKo ?? rarityLabelDefault(item.rarity);
    const tier = item.rarityDisplay?.tier ?? 1;

    const pitch = item.pitch ?? item.description ?? "";
    const lore = item.lore ?? [];

    const isCompact = variant === "compact";

    // 2026-04-25 Phase 32-C.6: 성공 직후 shimmer 1회
    const shimmer = useForgeShimmer(instance?.instanceId);
    const isShimmering = shimmer.active;
    const shimmerColor = instance
        ? shimmer.kind === "engrave"
            ? (instance.engraveTier === "GREAT" ? tokens.color.brass : tokens.color.moss)
            : resolveEquipmentAuraColor(instance.enhancement, instance.engraveTier)
        : rarityColor;

    return (
        <motion.article
            role="article"
            aria-label={`${rarityLabel} ${item.name}`}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={isShimmering
                ? {
                    opacity: 1,
                    y: 0,
                    scale: [1, 1.025, 1],
                    boxShadow: [tierShadow(tier, rarityColor), `0 0 36px ${shimmerColor}88`, tierShadow(tier, rarityColor)],
                    filter: ["brightness(1)", "brightness(1.12)", "brightness(1)"],
                }
                : { opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: isShimmering ? 1.4 : 0.35, ease: tokens.easing.decelerate }}
            className="relative flex flex-col rounded-m overflow-hidden"
            style={{
                width: isCompact ? 240 : 360,
                background: `${tokens.color.ink}F0`,
                border: tierBorder(tier, rarityColor),
                boxShadow: tierShadow(tier, rarityColor),
                wordBreak: "keep-all",
            }}
        >
            {/* 2026-04-25 Phase 32-C.6: 제련/각인 시각 보상 오라 — 카드 뒤에 깔림 */}
            {instance && (
                <EquipmentAura
                    enhancement={instance.enhancement}
                    engraveTier={instance.engraveTier}
                />
            )}
            <RarityHeader rarityColor={rarityColor} rarityLabel={rarityLabel} category={item.category} slot={item.slot ?? undefined} />

            <ArtSlot item={item} rarityColor={rarityColor} tier={tier} compact={isCompact} />

            <div className="px-m pt-m pb-s">
                <h3
                    className="font-display text-center"
                    style={{
                        color: rarityColor,
                        fontSize: isCompact ? tokens.type.size.small : tokens.type.size.subtitle,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        lineHeight: 1.2,
                    }}
                >
                    {item.name}
                    {instance && instance.enhancement > 0 && (
                        <span
                            className="ml-xs font-stat"
                            style={{ color: tokens.color.ember, fontSize: tokens.type.size.small }}
                        >
                            +{instance.enhancement}
                        </span>
                    )}
                </h3>
                <div
                    aria-hidden
                    className="mx-auto my-xs"
                    style={{
                        width: "50%",
                        height: 1,
                        background: `linear-gradient(90deg, transparent, ${tokens.color.brass}88, transparent)`,
                    }}
                />
                {pitch && (
                    <p
                        className="font-body italic text-center"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            lineHeight: tokens.type.lineHeight.snug,
                            opacity: 0.9,
                        }}
                    >
                        {pitch}
                    </p>
                )}
            </div>

            {!isCompact && runId && instance && (item.category === "WEAPON" || item.category === "ARMOR") && (
                <div
                    className="px-m py-s border-t"
                    style={{
                        borderColor: `${tokens.color.brass}24`,
                        display: "grid",
                        gap: 8,
                    }}
                >
                    <div className="flex items-center justify-between gap-s">
                        <SectionLabel>공방</SectionLabel>
                        <div className="font-body" style={{ color: tokens.color.parchmentDim, fontSize: 13.5 }}>
                            지금 · 다음
                        </div>
                    </div>
                    <div style={forgeCompareRow}>
                        <div style={forgeCompareBox(tokens.color.brass)}>
                            <span style={forgeCompareLabel}>제련</span>
                            <span style={forgeCompareValue}>+{instance.enhancement}</span>
                            <span style={forgeCompareHint}>다음 +{Math.min(10, instance.enhancement + 1)}</span>
                        </div>
                        {item.category === "WEAPON" && (
                            <div style={forgeCompareBox(tokens.color.moss)}>
                                <span style={forgeCompareLabel}>각인</span>
                                <span style={forgeCompareValue}>{instance.engraveId ? "새겨짐" : "비어 있음"}</span>
                                <span style={forgeCompareHint}>{instance.engraveId ? "다른 이름을 고른다" : "이름을 새긴다"}</span>
                            </div>
                        )}
                    </div>
                    <div style={forgeActionRow}>
                        {/* 2026-04-25: +10 만렙 도달 시 제련 버튼 비활성. 클릭 → API 에러 진입 차단. */}
                        {instance.enhancement >= 10 ? (
                            <button
                                disabled
                                className="rounded-pill"
                                style={{ ...forgeActionButton(tokens.color.brass), opacity: 0.4, cursor: "not-allowed" }}
                                title="이미 만렙(+10)에 도달했습니다"
                            >
                                🔨 제련 완료
                            </button>
                        ) : (
                            <button
                                onClick={() => setRefineModalOpen(true)}
                                className="rounded-pill"
                                style={forgeActionButton(tokens.color.brass)}
                            >
                                🔨 제련 열기
                            </button>
                        )}
                        {item.category === "WEAPON" && (
                            <button
                                onClick={() => setCeremonyModalOpen(true)}
                                className="rounded-pill"
                                style={forgeActionButton(tokens.color.moss)}
                            >
                                ✦ 각인 열기
                            </button>
                        )}
                        {item.category === "WEAPON" && (instance.engraveCracks ?? 0) > 0 && (
                            <CrackRepairButton
                                runId={runId}
                                instanceId={instance.instanceId}
                                cracks={instance.engraveCracks ?? 0}
                                onDone={onForgeComplete}
                                compact
                            />
                        )}
                    </div>
                </div>
            )}

            {!isCompact && (
                <EffectsBlock item={item} enhancement={instance?.enhancement ?? 0} />
            )}

            {!isCompact && !instance && item.randomization && item.randomization.affixes.length > 0 && (
                <RandomizationBlock item={item} />
            )}

            {/* 2026-04-24: 각인(engrave) 라이더 표시 — 인스턴스에 engraveId 있거나 편집 가능하면 노출. */}
            {!isCompact && (instance?.engraveId || onEngraveChange || (instance?.engraveCracks ?? 0) > 0) && (
                <EngraveBlock
                    engraveId={instance?.engraveId ?? null}
                    engraveTier={instance?.engraveTier ?? null}
                    engraveCracks={instance?.engraveCracks ?? 0}
                    canEdit={!!onEngraveChange}
                    onRequestEdit={onEngraveChange ? () => setEngravePickerOpen(true) : undefined}
                />
            )}

            {/* 2026-04-24: 각인 교체 modal — onEngraveChange 있을 때만. */}
            {engravePickerOpen && onEngraveChange && (
                <EngravePickerModal
                    currentEngraveId={instance?.engraveId ?? null}
                    currentGold={currentGold}
                    inventoryItems={inventoryItems}
                    onClose={() => setEngravePickerOpen(false)}
                    onPick={(nextId, materialInstanceId) => {
                        onEngraveChange(nextId, materialInstanceId);
                        setEngravePickerOpen(false);
                    }}
                />
            )}

            {!isCompact && runId && instance && (
                <div
                    className="px-m py-s border-t"
                    style={{ borderColor: `${tokens.color.parchmentDim}18` }}
                >
                    <div style={forgeSecondaryRow}>
                        <button
                            onClick={() => setSalvageModalOpen(true)}
                            className="font-body"
                            style={forgeSecondaryAction(tokens.color.burgundy)}
                            title="장비를 해체해 장인의 재와 재료를 돌려받습니다"
                        >
                            🪓 분해
                        </button>
                    </div>
                </div>
            )}

            {/* 2026-04-25: onComplete 가 발동하자마자 모달을 닫으면 blackout·bursting·FinalStage 가
                한 프레임만에 언마운트되어 유저가 결과 연출을 못 본다. 이제 onComplete 는 부모의
                데이터 갱신만 트리거하고, 모달 종료는 유저가 FinalStage 의 닫기 버튼을 눌러 onClose
                를 호출할 때만. */}
            {refineModalOpen && runId && instance && (
                <RefineModal
                    runId={runId}
                    item={instance}
                    inventoryItems={inventoryItems}
                    onClose={() => setRefineModalOpen(false)}
                    onComplete={() => { onForgeComplete?.(); }}
                />
            )}

            {ceremonyModalOpen && runId && instance && (
                <EngraveCeremonyModal
                    runId={runId}
                    item={instance}
                    inventoryItems={inventoryItems}
                    currentGold={currentGold}
                    onClose={() => setCeremonyModalOpen(false)}
                    onComplete={() => { onForgeComplete?.(); }}
                />
            )}

            {salvageModalOpen && runId && instance && (
                <SalvageConfirmModal
                    runId={runId}
                    item={instance}
                    onClose={() => setSalvageModalOpen(false)}
                    onComplete={() => { onForgeComplete?.(); setSalvageModalOpen(false); }}
                />
            )}

            {!isCompact && lore.length > 0 && (
                <LoreBlock lore={lore} />
            )}

            {!isCompact && hasRequirements(item) && (
                <RequirementsBlock item={item} currentStats={currentStats} />
            )}

            {(price !== undefined || context !== "readonly") && (
                <div
                    className="px-m py-s flex items-center justify-between gap-s"
                    style={{
                        background: `${tokens.color.ink}CC`,
                        borderTop: `1px solid ${tokens.color.parchment}22`,
                    }}
                >
                    {price !== undefined && (
                        <div className="flex items-baseline gap-xs">
                            <span
                                className="font-body"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.small,
                                    letterSpacing: tokens.type.letterSpacing.wide,
                                }}
                            >
                                {priceLabel(context)}
                            </span>
                            <span
                                className="font-stat"
                                style={{
                                    color: canAfford ? tokens.color.brass : tokens.color.burgundy,
                                    fontSize: tokens.type.size.small,
                                }}
                            >
                                {price.toLocaleString()}g
                            </span>
                        </div>
                    )}
                    <div className="flex gap-xs ml-auto">
                        {onAction && (
                            <PrimaryButton
                                size="s"
                                onClick={onAction}
                                disabled={actionDisabled || (context === "shop-buy" && !canAfford)}
                                variant={context === "shop-buy" && !canAfford ? "danger" : "primary"}
                                soundOnClick="commit"
                            >
                                {actionLabel(context, canAfford)}
                            </PrimaryButton>
                        )}
                        {onClose && (
                            <PrimaryButton size="s" variant="ghost" onClick={onClose}>
                                닫기
                            </PrimaryButton>
                        )}
                    </div>
                </div>
            )}
        </motion.article>
    );
}

/* ───────────────────────────── 섹션 컴포넌트 ───────────────────────────── */

function RarityHeader({
    rarityColor, rarityLabel, category, slot,
}: {
    rarityColor: string;
    rarityLabel: string;
    category: ItemCategory;
    slot?: string;
}) {
    return (
        <div
            className="flex items-center justify-between px-m relative"
            style={{
                height: 42,
                background: `linear-gradient(90deg, ${rarityColor}52, transparent 66%)`,
                borderBottom: `1px solid ${rarityColor}66`,
                boxShadow: `inset 0 1px 0 ${rarityColor}33, inset 0 -10px 24px ${tokens.color.ink}22`,
            }}
        >
            {/* 2026-04-23: accent dash + uppercase rarity — Spirytale 언어 통일. */}
            <span
                className="font-body flex items-center gap-[6px]"
                style={{
                    color: rarityColor,
                    fontSize: 15.5,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    textShadow: `0 0 10px ${rarityColor}44, ${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                <span
                    aria-hidden
                    style={{
                        width: 12,
                        height: 1,
                        background: rarityColor,
                        boxShadow: `0 0 8px ${rarityColor}`,
                        opacity: 0.8,
                    }}
                />
                {rarityLabel}
            </span>
            <span
                className="font-body"
                style={{
                    color: tokens.color.parchment,
                    fontSize: 16.5,
                    letterSpacing: tokens.type.letterSpacing.wide,
                    textShadow: `${tokens.color.ink} 0px 2px 8px`,
                }}
            >
                {categoryLabel(category)}{slot ? ` · ${slotLabel(slot)}` : ""}
            </span>
        </div>
    );
}

function ArtSlot({
    item, rarityColor, tier, compact,
}: {
    item: ItemDefinition;
    rarityColor: string;
    tier: number;
    compact: boolean;
}) {
    const [art, setArt] = useState(() => resolveItemArt(item.id, item.category));
    const [errored, setErrored] = useState(false);
    const size = compact ? 120 : 180;

    useEffect(() => {
        setArt(resolveItemArt(item.id, item.category));
        setErrored(false);
    }, [item.id, item.category]);

    return (
        <div
            className="relative mx-auto mt-m"
            style={{
                width: size,
                height: size,
                background: `radial-gradient(circle at 50% 50%, ${rarityColor}22, ${tokens.color.ink}CC)`,
                border: `1px solid ${rarityColor}66`,
                borderRadius: tokens.radius.m,
                boxShadow: tier >= 4 ? `0 0 24px ${rarityColor}44` : "none",
                overflow: "hidden",
            }}
        >
            {!errored ? (
                <img
                    src={art.src}
                    alt=""
                    onError={() => {
                        const fallback = resolveItemArt("", item.category);
                        if (art.src !== fallback.src) {
                            setArt(fallback);
                            return;
                        }
                        setErrored(true);
                    }}
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                    style={{
                        filter: tier >= 4 ? `drop-shadow(0 0 12px ${rarityColor}88)` : "none",
                    }}
                />
            ) : (
                <div
                    className="absolute inset-0 flex items-center justify-center select-none"
                    aria-hidden
                    style={{ color: rarityColor, fontSize: size * 0.42 }}
                >
                    {categoryEmoji(item.category)}
                </div>
            )}
            {art.mode !== "exact" && (
                <div
                    className="absolute inset-x-0 bottom-0 px-s py-[4px] text-center"
                    style={{
                        background: `${tokens.color.ink}CC`,
                        borderTop: `1px solid ${rarityColor}44`,
                        color: tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        textShadow: `${tokens.color.ink} 0px 2px 8px`,
                    }}
                >
                    {art.mode === "alias" ? "유사 도감 이미지" : "분류 이미지"}
                </div>
            )}
            {tier === 5 && (
                <motion.div
                    className="absolute inset-0 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.2, 0.6, 0.2] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        background: `radial-gradient(circle at 50% 50%, ${rarityColor}22, transparent 70%)`,
                    }}
                />
            )}
        </div>
    );
}

function EffectsBlock({ item, enhancement = 0 }: { item: ItemDefinition; enhancement?: number }) {
    const { statBonuses, derivedBonuses, specialEffects, procs, consumeRestore } = item.effects;
    const hasAny =
        Object.keys(statBonuses ?? {}).length > 0
        || Object.keys(derivedBonuses ?? {}).length > 0
        || (specialEffects?.length ?? 0) > 0
        || (procs?.length ?? 0) > 0
        || Object.keys(consumeRestore ?? {}).length > 0;
    if (!hasAny) return null;

    // 2026-04-25: enhancement (+N) 의 stat 분해 표시.
    //   - 무기 derivedBonus(physicalAttack/magicalAttack): 누진 곱셈 (StatFormula.weaponEnhancementMultiplier)
    //   - 방어구 derivedBonus(physicalDefense/magicalDefense): 선형 (1 + 0.08N)
    //   다른 stat 은 enhancement 영향 없음.
    const isWeaponDerived = (k: string) => k === "physicalAttack" || k === "magicalAttack";
    const isArmorDerived = (k: string) => k === "physicalDefense" || k === "magicalDefense";
    const enhancedDerivedKeys = (item.category === "WEAPON")
        ? new Set(["physicalAttack", "magicalAttack"])
        : (item.category === "ARMOR" ? new Set(["physicalDefense", "magicalDefense"]) : new Set<string>());
    const computeEnhancedBonus = (key: string, base: number): number => {
        if (enhancement <= 0 || !enhancedDerivedKeys.has(key)) return 0;
        const mult = isWeaponDerived(key)
            ? weaponEnhancementMultiplier(enhancement)
            : isArmorDerived(key)
                ? armorEnhancementMultiplier(enhancement)
                : 1.0;
        return Math.round(base * mult) - base;
    };

    return (
        <section
            className="px-m py-s"
            style={{ borderTop: `1px solid ${tokens.color.parchment}22` }}
        >
            <SectionLabel>효과</SectionLabel>
            <ul className="flex flex-col gap-[3px]">
                {Object.entries(statBonuses ?? {}).map(([k, v]) => (
                    <EffectLine key={`s-${k}`} label={labelForStat(k)} value={v} kind="stat" />
                ))}
                {Object.entries(derivedBonuses ?? {}).map(([k, v]) => (
                    <EffectLine
                        key={`d-${k}`}
                        label={derivedLabel(k)}
                        value={v}
                        kind="derived"
                        enhancementBonus={computeEnhancedBonus(k, v)}
                    />
                ))}
                {Object.entries(consumeRestore ?? {}).map(([k, v]) => (
                    <EffectLine key={`c-${k}`} label={`${restoreLabel(k)} 회복`} value={v} kind="restore" />
                ))}
                {(specialEffects ?? []).map((e, i) => (
                    <li
                        key={`sp-${i}`}
                        className="font-body flex items-start gap-xs"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            lineHeight: tokens.type.lineHeight.snug,
                        }}
                    >
                        <span aria-hidden style={{ color: tokens.color.ember, flexShrink: 0 }}>◆</span>
                        <span>{e}</span>
                    </li>
                ))}
                {(procs ?? []).map((p, i) => (
                    <li
                        key={`p-${i}`}
                        className="font-body flex items-start gap-xs"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            lineHeight: tokens.type.lineHeight.snug,
                        }}
                    >
                        <span aria-hidden style={{ color: tokens.color.ember, flexShrink: 0 }}>✦</span>
                        <span>{triggerLabel(p.trigger)} {Math.round(p.chance * 100)}% — {p.effect}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

/**
 * 2026-04-24: 각인(engrave) 블록 — 장비 인스턴스에 박힌 각인의 이름·의미·rider 효과를 표시.
 * ENGRAVE_META 에 없는 id 면 원문만 노출. combat-redesign-260423.md §0.3.
 * canEdit 이면 우상단 "각인 변경" 버튼 노출.
 */
function EngraveBlock({
    engraveId,
    engraveTier,
    engraveCracks,
}: {
    engraveId: string | null;
    engraveTier?: "SUCCESS" | "GREAT" | null;
    engraveCracks?: number;
    /** 2026-04-25 deprecated — Ceremony 플로우로 통일. */
    canEdit?: boolean;
    onRequestEdit?: () => void;
}) {
    const meta = engraveId ? ENGRAVE_META[engraveId] : null;
    const label = meta?.label ?? engraveId ?? "각인 없음";
    const meaning = meta?.meaning ?? "";
    const rider = meta?.rider ?? "";
    const cracks = engraveCracks ?? 0;
    const tierSymbol = engraveTier === "GREAT" ? "✦"
                     : engraveTier === "SUCCESS" ? "✧" : "";
    return (
        <section
            className="px-m py-s"
            style={{
                borderTop: `1px solid ${tokens.color.parchment}22`,
                background: `linear-gradient(180deg, ${tokens.color.ember}10, transparent 80%)`,
            }}
        >
            {/* 2026-04-25: "각인 변경" 레거시 버튼 제거. 각인 교체는 ForgeSection 의 "각인 의식" 을 통해서만. */}
            <div className="flex items-center justify-between">
                <SectionLabel>각인</SectionLabel>
            </div>
            <div className="flex items-center gap-s mt-xs">
                <span
                    className="font-display"
                    style={{
                        color: engraveId ? tokens.color.ember : tokens.color.parchmentDim,
                        fontSize: tokens.type.size.body,
                        letterSpacing: "0.08em",
                        textShadow: engraveId ? `0 0 10px ${tokens.color.ember}44` : undefined,
                    }}
                    >
                        ◆ {label}
                    {/* 2026-04-25 Phase 32-C.5: tier 배지 — GREAT 는 황금 glow */}
                    {tierSymbol && (
                        <span style={{
                            marginLeft: 6,
                            fontSize: 13,
                            color: engraveTier === "GREAT" ? tokens.color.brass : tokens.color.parchment,
                            textShadow: engraveTier === "GREAT" ? `0 0 8px ${tokens.color.brass}` : undefined,
                        }}>
                            {tierSymbol}
                        </span>
                    )}
                </span>
            </div>
            {meaning && (
                <div
                    className="font-body italic mt-[4px]"
                    style={{
                        color: tokens.color.parchmentDim,
                        fontSize: 12.5,
                        lineHeight: 1.45,
                        opacity: 0.86,
                    }}
                >
                    {meaning}
                </div>
            )}
            {rider && (
                <div
                    className="font-body mt-xs"
                    style={{
                        color: tokens.color.parchment,
                        fontSize: tokens.type.size.small,
                        lineHeight: tokens.type.lineHeight.snug,
                        opacity: 0.92,
                    }}
                >
                    {rider}
                </div>
            )}
            {/* 2026-04-25 Phase 32-C.5: 균열 시각 표시 — 각 카운트당 금간 선 */}
            {cracks > 0 && (
                <div className="mt-xs flex items-center gap-xs" style={{ opacity: 0.9 }}>
                    <span style={{
                        color: tokens.color.burgundy,
                        fontSize: 13,
                        letterSpacing: 2,
                    }}>
                        {"𓈈".repeat(Math.min(cracks, 5))}
                    </span>
                    <span style={{
                        color: tokens.color.burgundy,
                        fontSize: tokens.type.size.tiny,
                        fontStyle: "italic",
                    }}>
                        균열 {cracks}개 — 다음 대실패율 +{cracks * 10}%
                    </span>
                </div>
            )}
        </section>
    );
}

/**
 * 2026-04-24: 각인 선택 모달. 11종 + 제거 옵션.
 */
function EngravePickerModal({
    currentEngraveId,
    currentGold,
    inventoryItems,
    onClose,
    onPick,
}: {
    currentEngraveId: string | null;
    currentGold?: number;
    inventoryItems?: InventoryItemView[];
    onClose: () => void;
    onPick: (engraveId: string | null, materialInstanceId: string | null) => void;
}) {
    const options = Object.values(ENGRAVE_META);
    // 2026-04-24: v2 content 로드 — 각 각인의 "standard" 등급 재료 이름을 표시.
    const v2 = useV2Content();
    // 비용 차등 — 가장 싼 (확률) 기준으로 "부족" 표시. 부족해도 싼 각인은 선택 가능.
    const minCost = 100;
    const canAffordMin = currentGold === undefined || currentGold >= minCost;

    /**
     * 2026-04-24: 인벤토리에서 해당 engrave 에 쓸 수 있는 재료 instance 중 가장 높은 등급을 반환.
     * 등급 우선순위: crystal > refined > standard. 같은 등급이면 먼저 발견된 것.
     *   standard → 50% / refined → 65% / crystal → 80% 할인.
     */
    const findMaterialInstance = (engraveId: string): { inst: InventoryItemView; grade: "standard" | "refined" | "crystal"; discount: number } | null => {
        if (!inventoryItems || inventoryItems.length === 0) return null;
        let bestInst: InventoryItemView | null = null;
        let bestGrade: "standard" | "refined" | "crystal" | null = null;
        for (const it of inventoryItems) {
            if (it.quantity <= 0) continue;
            const tags = it.definition?.tags ?? [];
            if (!tags.includes("engrave-material") || !tags.includes(engraveId)) continue;
            const grade: "standard" | "refined" | "crystal" =
                tags.includes("crystal") ? "crystal" :
                tags.includes("refined") ? "refined" :
                "standard";
            // priority compare
            const rank = (g: string) => g === "crystal" ? 3 : g === "refined" ? 2 : 1;
            if (!bestInst || rank(grade) > rank(bestGrade ?? "standard")) {
                bestInst = it;
                bestGrade = grade;
            }
        }
        if (!bestInst || !bestGrade) return null;
        const discount = bestGrade === "crystal" ? 0.80 : bestGrade === "refined" ? 0.65 : 0.50;
        return { inst: bestInst, grade: bestGrade, discount };
    };
    return (
        <div
            className="fixed inset-0 z-modal flex items-center justify-center"
            style={{ background: `${tokens.color.ink}DD`, backdropFilter: "blur(4px)" }}
            onClick={onClose}
        >
            <div
                className="rounded-l p-l max-w-[640px] w-full max-h-[80vh] overflow-y-auto"
                style={{
                    background: `linear-gradient(180deg, ${tokens.color.ink}F8, ${tokens.color.ink}EE)`,
                    border: `1px solid ${tokens.color.ember}44`,
                    boxShadow: `0 20px 60px ${tokens.color.ink}`,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-baseline justify-between mb-m">
                    <div
                        className="font-display"
                        style={{
                            color: tokens.color.ember,
                            fontSize: tokens.type.size.subtitle,
                            letterSpacing: "0.08em",
                            textShadow: `0 0 10px ${tokens.color.ember}44`,
                        }}
                    >
                        각인 선택
                    </div>
                    {/* 보유 금화 표시 — 최저가(100) 도 못 살 때 burgundy. 각인별 비용은 아래 옵션에 개별 표시. */}
                    <div
                        className="font-stat"
                        style={{
                            color: canAffordMin ? tokens.color.brass : tokens.color.burgundy,
                            fontSize: tokens.type.size.small,
                            letterSpacing: "0.06em",
                        }}
                        title="각인별 비용: 확률 100g · 조건 140g · 상시 200g · 제거 무료."
                    >
                        ¤ 보유 {currentGold ?? "—"}
                    </div>
                </div>
                <div className="flex flex-col gap-xs">
                    <button
                        type="button"
                        onClick={() => onPick(null, null)}
                        className="text-left p-s rounded-s"
                        style={{
                            background: currentEngraveId === null ? `${tokens.color.parchmentDim}22` : "transparent",
                            border: `1px solid ${tokens.color.parchmentDim}44`,
                            cursor: "pointer",
                        }}
                    >
                        <div className="font-display" style={{ color: tokens.color.parchmentDim, fontSize: tokens.type.size.small }}>
                            ◇ 각인 제거
                        </div>
                        <div className="font-body mt-xs" style={{ color: tokens.color.parchmentDim, fontSize: tokens.type.size.tiny, opacity: 0.7 }}>
                            각인 없는 원래 상태로 되돌립니다.
                        </div>
                    </button>
                    {options.map((opt) => {
                        const active = currentEngraveId === opt.id;
                        const baseCost = engraveForgeCost(opt.id);
                        const matResult = findMaterialInstance(opt.id);
                        const effectiveCost = matResult
                            ? Math.round(baseCost * (1 - matResult.discount))
                            : baseCost;
                        const disabled = !active && currentGold !== undefined && currentGold < effectiveCost;
                        // 해당 재료 등급 정보.
                        const matDef = matResult ? findEngraveMaterials(v2, opt.id).find(m => m.grade === matResult.grade) : null;
                        const discountPct = matResult ? Math.round(matResult.discount * 100) : 0;
                        return (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => {
                                    if (active) return; // 동일 각인 재선택은 비용 낭비 방지.
                                    if (disabled) return;
                                    onPick(opt.id, matResult?.inst.instanceId ?? null);
                                }}
                                disabled={disabled || active}
                                className="text-left p-s rounded-s"
                                style={{
                                    background: active ? `${tokens.color.ember}22` : `${tokens.color.parchment}04`,
                                    border: `1px solid ${active ? tokens.color.ember : tokens.color.parchmentDim}44`,
                                    cursor: disabled || active ? "not-allowed" : "pointer",
                                    opacity: disabled ? 0.45 : 1,
                                }}
                            >
                                <div className="flex items-center gap-s">
                                    <span
                                        className="font-display"
                                        style={{
                                            color: tokens.color.ember,
                                            fontSize: tokens.type.size.small,
                                            letterSpacing: "0.06em",
                                        }}
                                    >
                                        ◆ {opt.label}
                                    </span>
                                    <span
                                        className="font-body italic"
                                        style={{ color: tokens.color.parchmentDim, fontSize: tokens.type.size.tiny, opacity: 0.75 }}
                                    >
                                        — {opt.meaning}
                                    </span>
                                    <span
                                        className="font-stat"
                                        style={{
                                            marginLeft: "auto",
                                            color: disabled ? tokens.color.burgundy : tokens.color.brass,
                                            fontSize: tokens.type.size.tiny,
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        {matResult ? (
                                            <>
                                                <span style={{ textDecoration: "line-through", opacity: 0.5, marginRight: 4 }}>¤ {baseCost}</span>
                                                <span style={{ color: tokens.color.moss }}>¤ {effectiveCost}</span>
                                            </>
                                        ) : (
                                            <>¤ {baseCost}</>
                                        )}
                                    </span>
                                </div>
                                <div
                                    className="font-body mt-xs"
                                    style={{
                                        color: tokens.color.parchment,
                                        fontSize: tokens.type.size.tiny,
                                        lineHeight: tokens.type.lineHeight.snug,
                                    }}
                                >
                                    {opt.rider}
                                </div>
                                {matDef ? (() => {
                                    // O-1: 성공 확률 등급별 — standard 80 / refined 90 / crystal 97.
                                    const successRate = matResult?.grade === "crystal" ? 97
                                        : matResult?.grade === "refined" ? 90 : 80;
                                    return (
                                        <div
                                            className="font-body mt-xs"
                                            style={{
                                                color: tokens.color.moss,
                                                fontSize: tokens.type.size.tiny,
                                                letterSpacing: "0.04em",
                                                opacity: 0.9,
                                            }}
                                            title={matDef.shortDesc + " · " + matDef.acquisitionHint}
                                        >
                                            ◆ 재료 사용: {matDef.label} (1개 소모, {discountPct}% 할인, 성공 {successRate}%)
                                        </div>
                                    );
                                })() : (() => {
                                    const stdMat = findEngraveMaterials(v2, opt.id).find(m => m.grade === "standard");
                                    return stdMat ? (
                                        <div
                                            className="font-body mt-xs"
                                            style={{
                                                color: tokens.color.parchmentDim,
                                                fontSize: tokens.type.size.tiny,
                                                letterSpacing: "0.04em",
                                                opacity: 0.7,
                                            }}
                                            title={stdMat.shortDesc + " · " + stdMat.acquisitionHint}
                                        >
                                            재료 없음 (성공 100%) — {stdMat.label} 50%/80% · 정제 65%/90% · 결정 80%/97%
                                        </div>
                                    ) : null;
                                })()}
                            </button>
                        );
                    })}
                </div>
                <div className="mt-m flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="font-body"
                        style={{
                            padding: "6px 16px",
                            fontSize: tokens.type.size.small,
                            color: tokens.color.parchmentDim,
                            background: "transparent",
                            border: `1px solid ${tokens.color.parchmentDim}55`,
                            borderRadius: 4,
                            cursor: "pointer",
                        }}
                    >
                        취소
                    </button>
                </div>
            </div>
        </div>
    );
}

function LoreBlock({ lore }: { lore: string[] }) {
    // 2026-04-25: D4 식 로어 토글. 기본 = 접힘. 유저가 능력치만 빠르게 훑기 좋게.
    const [open, setOpen] = useState(false);
    return (
        <section
            className="px-m py-s"
            style={{
                borderTop: `1px solid ${tokens.color.parchment}22`,
                maxHeight: open ? 280 : 60,
                overflowY: open ? "auto" : "hidden",
                transition: "max-height 0.25s ease",
            }}
        >
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="flex items-center justify-between w-full text-left"
                style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                }}
            >
                <SectionLabel>기록</SectionLabel>
                <span
                    aria-hidden
                    style={{
                        color: tokens.color.parchmentDim,
                        fontSize: 12,
                        opacity: 0.7,
                    }}
                >
                    {open ? "▾" : `▸ ${lore.length}장`}
                </span>
            </button>
            {open && (
                <div className="flex flex-col gap-xs mt-xs">
                    {lore.map((para, i) => (
                        <p
                            key={i}
                            className="font-body italic"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: tokens.type.size.small,
                                lineHeight: tokens.type.lineHeight.normal,
                                opacity: 0.92,
                                textShadow: `${tokens.color.ink} 0px 2px 8px`,
                            }}
                        >
                            {para}
                        </p>
                    ))}
                </div>
            )}
        </section>
    );
}

function RandomizationBlock({ item }: { item: ItemDefinition }) {
    const randomization = item.randomization;
    // 2026-04-25: 변주 블록도 기본 접힘 (D4 식 토글)
    const [open, setOpen] = useState(false);
    if (!randomization || randomization.affixes.length === 0) return null;

    return (
        <section
            className="px-m py-s"
            style={{
                borderTop: `1px solid ${tokens.color.parchment}22`,
                maxHeight: open ? 280 : 60,
                overflowY: open ? "auto" : "hidden",
                transition: "max-height 0.25s ease",
            }}
        >
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="flex items-center justify-between w-full text-left"
                style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
            >
                <SectionLabel>깃들 수 있는 변주</SectionLabel>
                <span
                    aria-hidden
                    style={{
                        color: tokens.color.parchmentDim,
                        fontSize: 12,
                        opacity: 0.7,
                    }}
                >
                    {open ? "▾" : `▸ ${randomization.minRolls}~${randomization.maxRolls}개`}
                </span>
            </button>
            {open && (
                <div
                    className="font-body mb-xs mt-xs"
                    style={{
                        color: tokens.color.parchmentDim,
                        fontSize: 12.5,
                        lineHeight: tokens.type.lineHeight.normal,
                    }}
                >
                    임의로 깃드는 affix 풀:
                </div>
            )}
            {open && <div className="flex flex-col gap-xs">
                {randomization.affixes.map(affix => (
                    <div
                        key={affix.id}
                        className="rounded-s px-s py-xs"
                        style={{
                            background: `${tokens.color.ink}99`,
                            border: `1px solid ${tokens.color.brass}22`,
                        }}
                    >
                        <div
                            className="font-body"
                            style={{
                                color: tokens.color.parchment,
                                fontSize: tokens.type.size.small,
                            }}
                        >
                            {affix.name}
                        </div>
                        {affix.description && (
                            <div
                                className="font-body mt-[2px]"
                                style={{
                                    color: tokens.color.parchment,
                                    fontSize: tokens.type.size.small,
                                    lineHeight: tokens.type.lineHeight.normal,
                                    opacity: 0.9,
                                }}
                            >
                                {affix.description}
                            </div>
                        )}
                    </div>
                ))}
            </div>}
        </section>
    );
}

function RequirementsBlock({
    item, currentStats,
}: {
    item: ItemDefinition;
    currentStats?: Record<string, number>;
}) {
    const { requiredStats, classCardIds } = item.requirements;
    const statEntries = Object.entries(requiredStats ?? {});
    if (statEntries.length === 0 && (!classCardIds || classCardIds.length === 0)) return null;

    return (
        <section
            className="px-m py-s"
            style={{ borderTop: `1px solid ${tokens.color.parchment}22` }}
        >
            <SectionLabel>착용 조건</SectionLabel>
            <div className="flex flex-col gap-[3px]">
                {statEntries.map(([k, required]) => {
                    const current = currentStats?.[k];
                    const met = current === undefined || current >= required;
                    return (
                        <div
                            key={k}
                            className="font-body flex items-center gap-xs"
                            style={{
                                color: met ? tokens.color.parchment : tokens.color.burgundy,
                                fontSize: tokens.type.size.small,
                            }}
                        >
                            <span aria-hidden style={{ color: met ? tokens.color.moss : tokens.color.burgundy }}>
                                {met ? "✓" : "✗"}
                            </span>
                            <span>
                                {labelForStat(k)} {required}
                                {current !== undefined && !met && (
                                    <span style={{ opacity: 0.75 }}> (현재 {current})</span>
                                )}
                            </span>
                        </div>
                    );
                })}
                {classCardIds && classCardIds.length > 0 && (
                    <div
                        className="font-body"
                        style={{
                            color: tokens.color.parchment,
                            fontSize: tokens.type.size.small,
                            marginTop: 4,
                            opacity: 0.92,
                            textShadow: `${tokens.color.ink} 0px 2px 8px`,
                        }}
                    >
                        깃들 수 있는 영혼이 정해져 있습니다.
                    </div>
                )}
            </div>
        </section>
    );
}

function EffectLine({
    label, value, kind, enhancementBonus = 0,
}: {
    label: string;
    value: number;
    kind: "stat" | "derived" | "restore";
    /** 2026-04-25: 제련 (+N) 으로 추가된 양. > 0 이면 우측에 brass 색으로 별도 표기. */
    enhancementBonus?: number;
}) {
    const positive = value >= 0;
    const color = !positive
        ? tokens.color.burgundy
        : kind === "stat"
            ? tokens.color.ember
            : kind === "restore"
                ? tokens.color.moss
                : tokens.color.parchment;
    const totalAfterEnhancement = value + enhancementBonus;
    return (
        <div
            className="font-body flex items-baseline justify-between"
            style={{
                color: tokens.color.parchment,
                fontSize: tokens.type.size.small,
            }}
        >
            <span>{label}</span>
            <span className="font-stat" style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ color }}>
                    {positive ? "+" : ""}{value}
                </span>
                {/* 2026-04-25: 제련 보너스 — brass 색으로 +N 표기 후 → 총합 까지. */}
                {enhancementBonus > 0 && (
                    <>
                        <span style={{ color: tokens.color.brass, opacity: 0.85 }}>
                            +{enhancementBonus}
                        </span>
                        <span style={{ color: tokens.color.brass, fontSize: 10, opacity: 0.6 }}>
                            = {positive ? "+" : ""}{totalAfterEnhancement}
                        </span>
                    </>
                )}
            </span>
        </div>
    );
}

/**
 * 2026-04-25: 백엔드 StatFormula.weaponEnhancementMultiplier / armorEnhancementMultiplier 미러링.
 *   여기서 곱한 값은 EffectLine 의 "+N" 표시와 일치한다. 백엔드 공식이 바뀌면 같이 갱신.
 */
function weaponEnhancementMultiplier(enhancement: number): number {
    const e = Math.max(0, Math.min(10, enhancement));
    if (e <= 4) return 1.0 + 0.10 * e;
    if (e <= 7) return 1.40 + 0.15 * (e - 4);
    return 1.85 + 0.30 * (e - 7);
}
function armorEnhancementMultiplier(enhancement: number): number {
    const e = Math.max(0, Math.min(10, enhancement));
    return 1.0 + 0.08 * e;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <div
            className="font-body flex items-center gap-[6px] mb-xs"
            style={{
                color: tokens.color.brass,
                fontSize: 13.5,
                letterSpacing: "0.18em",
                textShadow: `${tokens.color.ink} 0px 2px 8px`,
            }}
        >
            {/* 2026-04-23: accent dash — 섹션 구분을 Spirytale 언어로 통일. */}
            <span
                aria-hidden
                style={{
                    width: 10,
                    height: 1,
                    background: tokens.color.brass,
                    boxShadow: `0 0 6px ${tokens.color.brass}`,
                    opacity: 0.7,
                }}
            />
            {children}
        </div>
    );
}

/* ───────────────────────────── 유틸 ───────────────────────────── */

function paletteColor(token?: "slate" | "moss" | "brass" | "ember" | "burgundy"): string {
    switch (token) {
        case "moss": return tokens.color.moss;
        case "brass": return tokens.color.brass;
        case "ember": return tokens.color.ember;
        case "burgundy": return tokens.color.burgundy;
        case "slate":
        default: return tokens.color.parchmentDim;
    }
}

function rarityLabelDefault(r: string): string {
    switch (r) {
        case "COMMON": return "흔함";
        case "UNCOMMON": return "예사";
        case "RARE": return "드묾";
        case "EPIC": return "매우 드묾";
        case "LEGENDARY": return "전설";
        default: return r;
    }
}

function tierBorder(tier: number, color: string): string {
    if (tier >= 5) return `3px solid ${color}`;
    if (tier >= 4) return `2px solid ${color}`;
    if (tier >= 3) return `2px solid ${color}`;
    return `1px solid ${color}88`;
}

function tierShadow(tier: number, color: string): string {
    if (tier >= 5) return `0 0 32px ${color}66, inset 0 0 20px ${color}22`;
    if (tier >= 4) return `0 0 18px ${color}44, inset 0 0 14px ${color}18`;
    if (tier >= 3) return `inset 0 0 12px ${color}12`;
    return "none";
}

function categoryLabel(category: ItemCategory): string {
    switch (category) {
        case "WEAPON": return "무기";
        case "ARMOR": return "갑옷";
        case "ACCESSORY": return "장신구";
        case "CHARM": return "부적";
        case "CONSUMABLE": return "소모품";
        case "MATERIAL": return "재료";
        case "KEY": return "열쇠";
        default: return category;
    }
}

function categoryEmoji(category: ItemCategory): string {
    switch (category) {
        case "WEAPON": return "⚔";
        case "ARMOR": return "🛡";
        case "ACCESSORY": return "◈";
        case "CHARM": return "✦";
        case "CONSUMABLE": return "✿";
        case "MATERIAL": return "◆";
        case "KEY": return "⚷";
        default: return "◯";
    }
}

function slotLabel(slot: string): string {
    switch (slot) {
        case "WEAPON_MAIN": return "주무기";
        case "WEAPON_OFF": return "보조";
        case "ARMOR_HEAD": return "머리";
        case "ARMOR_BODY": return "몸통";
        case "ARMOR_HANDS": return "손";
        case "ARMOR_FEET": return "발";
        case "ACCESSORY_1": return "첫 장신구";
        case "ACCESSORY_2": return "둘째 장신구";
        case "CHARM": return "부적";
        default: return slot;
    }
}

function priceLabel(ctx: ItemContext): string {
    switch (ctx) {
        case "shop-buy": return "사는 값";
        case "shop-sell": return "되파는 값";
        default: return "값";
    }
}

function actionLabel(ctx: ItemContext, canAfford: boolean): string {
    switch (ctx) {
        case "shop-buy": return canAfford ? "들여온다" : "금화가 모자랍니다";
        case "shop-sell": return "내다 판다";
        case "inventory-equip": return "착용한다";
        case "inventory-consume": return "사용한다";
        case "event-reward": return "받아 든다";
        default: return "확인";
    }
}

function hasRequirements(item: ItemDefinition): boolean {
    const { requiredStats, classCardIds } = item.requirements;
    return Object.keys(requiredStats ?? {}).length > 0
        || (classCardIds && classCardIds.length > 0);
}

function derivedLabel(code: string): string {
    const map: Record<string, string> = {
        maxHp: "최대 체력",
        maxMp: "최대 정신력",
        physicalAttack: "물리 공격",
        magicalAttack: "마법 공격",
        physicalDefense: "물리 방어",
        magicalDefense: "마법 방어",
        accuracy: "명중률 %",
        evasion: "회피율 %",
        criticalChance: "치명타 %",
        actionDelay: "행동 지연",
        moveSpeed: "이동 속도",
        attackSpeed: "공격 속도",
        hitChance: "명중 %",
        critChance: "치명타 %",
        rangeMax: "최대 사거리",
        strategyExecution: "전술 이행률",
        magicCost: "마법 비용",
        firearmJamChance: "총포 고장률",
        healingReceived: "회복 배수",
        staggerResist: "경직 저항",
        carryWeight: "소지 무게",
    };
    return map[code] ?? code;
}

function restoreLabel(code: string): string {
    switch (code) {
        case "hp": return "체력";
        case "mp": return "정신력";
        case "stamina": return "기력";
        case "mana": return "정신";
        default: return code;
    }
}

function triggerLabel(trigger: string): string {
    switch (trigger) {
        case "ON_HIT": return "적중 시";
        case "ON_CRIT": return "치명타 시";
        case "ON_KILL": return "처치 시";
        case "ON_DAMAGED": return "피격 시";
        case "ON_TURN_START": return "턴 시작 시";
        case "ON_EQUIP": return "장착 시";
        default: return trigger;
    }
}


/** 2026-04-25 Phase 32 P1: 균열 복구 버튼. 수리석 1개 차감 → cracks -= 1. */
function CrackRepairButton({ runId, instanceId, cracks, onDone, compact = false }: {
    runId: string;
    instanceId: string;
    cracks: number;
    onDone?: () => void;
    compact?: boolean;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const handle = async () => {
        setBusy(true);
        try {
            await repairCrack(runId, instanceId);
            onDone?.();
        } catch (e) {
            const d = describeApiError(e);
            setError(d.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            onClick={handle}
            disabled={busy}
            className={compact ? "font-body" : "flex-1 rounded-m"}
            style={{
                ...(compact ? forgeSecondaryAction(tokens.color.ember) : forgeEntryButton(tokens.color.ember)),
                cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.65 : 1,
            }}
            title={error ?? "수리석 1개로 균열 1개 복구"}
        >
            {compact ? (
                <>🪨 균열 복구</>
            ) : (
                <>
                    <span style={forgeEntryKicker(tokens.color.ember)}>🪨 균열 복구</span>
                    <span style={forgeEntryTitle}>갈라진 결을 잇는다</span>
                    <span style={forgeEntryBody}>
                        지금 남은 균열 {cracks}개. 수리석으로 다음 각인 의식의 부담을 덜어 냅니다.
                    </span>
                </>
            )}
        </button>
    );
}

const forgeCompareRow: React.CSSProperties = {
    display: "grid",
    gap: 8,
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
};

const forgeActionRow: React.CSSProperties = {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
};

const forgeSecondaryRow: React.CSSProperties = {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
};

function forgeEntryButton(accent: string): React.CSSProperties {
    return {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        padding: "14px 16px",
        background: `linear-gradient(180deg, ${accent}14, rgba(255,255,255,0.02))`,
        color: tokens.color.parchment,
        border: `1px solid ${accent}66`,
        boxShadow: `0 0 22px ${accent}18`,
        textAlign: "left",
        width: "100%",
    };
}

function forgeSecondaryAction(accent: string): React.CSSProperties {
    return {
        padding: "8px 12px",
        background: `${accent}10`,
        color: tokens.color.parchment,
        border: `1px solid ${accent}55`,
        borderRadius: 10,
        cursor: "pointer",
        fontSize: 13,
        letterSpacing: "0.04em",
        textShadow: `${tokens.color.ink} 0px 2px 8px`,
    };
}

function forgeCompareBox(accent: string): React.CSSProperties {
    return {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "10px 12px",
        borderRadius: 10,
        background: `${accent}10`,
        border: `1px solid ${accent}33`,
    };
}

function forgeActionButton(accent: string): React.CSSProperties {
    return {
        padding: "10px 14px",
        background: `${accent}14`,
        color: tokens.color.parchment,
        border: `1px solid ${accent}55`,
        cursor: "pointer",
        fontSize: 14.5,
        letterSpacing: "0.04em",
        textShadow: `${tokens.color.ink} 0px 2px 8px`,
        boxShadow: `0 0 16px ${accent}18`,
    };
}

const forgeCompareLabel: React.CSSProperties = {
    color: tokens.color.parchmentDim,
    fontSize: 12.5,
    letterSpacing: "0.08em",
    textShadow: `${tokens.color.ink} 0px 2px 8px`,
};

const forgeCompareValue: React.CSSProperties = {
    color: tokens.color.parchment,
    fontSize: 16.5,
    letterSpacing: "0.04em",
    textShadow: `${tokens.color.ink} 0px 2px 8px`,
};

const forgeCompareHint: React.CSSProperties = {
    color: tokens.color.parchmentDim,
    fontSize: 13,
    lineHeight: 1.4,
};

function forgeEntryKicker(accent: string): React.CSSProperties {
    return {
        color: accent,
        fontSize: tokens.type.size.tiny,
        letterSpacing: "0.08em",
        textShadow: `${tokens.color.ink} 0px 2px 8px`,
    };
}

const forgeEntryTitle: React.CSSProperties = {
    color: tokens.color.parchment,
    fontSize: tokens.type.size.small,
    lineHeight: tokens.type.lineHeight.tight,
    textShadow: `${tokens.color.ink} 0px 2px 8px`,
};

const forgeEntryBody: React.CSSProperties = {
    color: tokens.color.parchment,
    fontSize: tokens.type.size.tiny,
    lineHeight: tokens.type.lineHeight.normal,
    opacity: 0.92,
    textShadow: `${tokens.color.ink} 0px 2px 8px`,
};
